// src/app/services/voucherService.ts
import axiosInstance from '@/app/api/axiosInstance';
import { PaginatedResponse, ListQueryParams } from '@/app/types/api';
import { Voucher, VoucherCreate, VoucherUpdate, VoucherResponse, VoucherEntryResponse } from '@/app/types/voucher';
import { VoucherType, VoucherTypeCreate, VoucherTypeUpdate, VoucherTypeResponse } from '@/app/types/voucherType';
import { PaymentAllocationResponse } from '@/app/types/payment';

// --- Voucher Type Service Functions ---

export const listVoucherTypes = async (params: ListQueryParams): Promise<PaginatedResponse<VoucherTypeResponse>> => {
  // Backend filters by org ID via auth context
  const response = await axiosInstance.get<PaginatedResponse<VoucherTypeResponse>>('/voucher-types/', { params });
  return response.data;
};

export const getVoucherType = async (id: number): Promise<VoucherTypeResponse> => {
  const response = await axiosInstance.get<VoucherTypeResponse>(`/voucher-types/${id}`);
  return response.data;
};

export const createVoucherType = async (data: VoucherTypeCreate): Promise<VoucherTypeResponse> => {
  const response = await axiosInstance.post<VoucherTypeResponse>('/voucher-types/', data);
  return response.data;
};

export const updateVoucherType = async (id: number, data: VoucherTypeUpdate): Promise<VoucherTypeResponse> => {
  const response = await axiosInstance.patch<VoucherTypeResponse>(`/voucher-types/${id}`, data);
  return response.data;
};

export const deleteVoucherType = async (id: number): Promise<void> => {
  await axiosInstance.delete(`/voucher-types/${id}`);
};

// --- Voucher Service Functions ---

export const listVouchers = async (params: ListQueryParams): Promise<PaginatedResponse<VoucherResponse>> => {
  // Backend filters by org ID via auth context
  console.log('listVouchers API call params:', params);
  console.log('Filters being sent:', params.filters);
  const response = await axiosInstance.get<PaginatedResponse<VoucherResponse>>('/vouchers/', { params });
  // Convert total_amount from string (if needed) to number
  response.data.items = response.data.items.map(v => ({
    ...v,
    total_amount: parseFloat(String(v.total_amount)),
  }));
  return response.data;
};

export const getVoucher = async (id: number): Promise<VoucherResponse> => {
  const response = await axiosInstance.get<VoucherResponse>(`/vouchers/${id}`);
  response.data.total_amount = parseFloat(String(response.data.total_amount));
  return response.data;
};

export const createVoucher = async (data: VoucherCreate): Promise<VoucherResponse> => {
  // Convert total_amount to string for backend Decimal field if necessary
  const payload = {
    ...data,
    total_amount: String(data.total_amount),
  };
  const response = await axiosInstance.post<VoucherResponse>('/vouchers/', payload);
  response.data.total_amount = parseFloat(String(response.data.total_amount));
  return response.data;
};

export const updateVoucher = async (id: number, data: VoucherUpdate): Promise<VoucherResponse> => {
  const payload = {
    ...data,
    ...(data.total_amount !== undefined && { total_amount: String(data.total_amount) }),
  };
  const response = await axiosInstance.patch<VoucherResponse>(`/vouchers/${id}`, payload);
  response.data.total_amount = parseFloat(String(response.data.total_amount));
  return response.data;
};

export const cancelVoucher = async (id: number): Promise<VoucherResponse> => {
  const response = await axiosInstance.post<VoucherResponse>(`/vouchers/${id}/cancel`);
  response.data.total_amount = parseFloat(String(response.data.total_amount));
  return response.data;
};

export const deleteVoucher = async (id: number): Promise<void> => {
  // Use with caution, prefer cancellation
  await axiosInstance.delete(`/vouchers/${id}`);
};

// --- Override OTP Service Functions ---

export const generateOverrideOTP = async (partyId?: number, newInvoiceAmount?: number): Promise<{
  success: boolean;
  message: string;
  expires_in_minutes: number;
}> => {
  let url = '/vouchers/generate-override-otp';
  const params = new URLSearchParams();

  if (partyId) {
    params.append('party_id', partyId.toString());
  }

  if (newInvoiceAmount !== undefined) {
    params.append('new_invoice_amount', newInvoiceAmount.toString());
  }

  if (params.toString()) {
    url += '?' + params.toString();
  }

  const response = await axiosInstance.post(url);
  return response.data;
};

// --- Payment Allocation Service Functions ---

export const listPaymentAllocations = async (paymentVoucherId: number): Promise<PaymentAllocationResponse[]> => {
  try {
    const response = await axiosInstance.get('/payments/payment-allocations', {
      params: { payment_voucher_id: paymentVoucherId }
    });
    return response.data.items || response.data; // Adapt based on backend response structure
  } catch (error) {
    console.error(`Error fetching allocations for payment ${paymentVoucherId}:`, error);
    throw error;
  }
};

// Fetch payment allocations for a specific invoice
export const listInvoicePaymentAllocations = async (invoiceVoucherId: number): Promise<PaymentAllocationResponse[]> => {
  try {
    const response = await axiosInstance.get('/payments/invoice-allocations', {
      params: { invoice_voucher_id: invoiceVoucherId }
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching allocations for invoice ${invoiceVoucherId}:`, error);
    throw error;
  }
};

// --- Voucher Entry (Journal Entry) Service Functions ---

export const listVoucherEntries = async (voucherId: number): Promise<VoucherEntryResponse[]> => {
  try {
    const response = await axiosInstance.get<VoucherEntryResponse[]>(`/vouchers/${voucherId}/entries`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching entries for voucher ${voucherId}:`, error);
    throw error;
  }
}; 